import React, { useState } from 'react';
import {
  Box, Grid, Button, Typography, Popover, Input, Select, MenuItem, InputLabel, FormControl, Chip, useMediaQuery,
  useTheme,
} from '@mui/material';
import { ChromePicker } from 'react-color';
import { useNavigate } from 'react-router-dom';
import { Checkbox, ListItemText } from '@mui/material';
import { useTranslation } from 'react-i18next';
export default function RouteAddDetails({
  routeData,
  onRouteDataChange,
  stops,
  vehicles,
  onVehicleSelect,
  onSubmit
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isExtraSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  // Ensure vehicles is always an array, even if it comes as an object
  const vehicleList = Array.isArray(vehicles) ? vehicles : vehicles.vehicles || [];

  const [anchorEl, setAnchorEl] = useState(null);
  console.log(routeData)

  // Add state for validation errors
  const [errors, setErrors] = useState({
    routeId: false,
    routeName: false,
    origin: false,
    destination: false
  });

  const handleColorPickerOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleColorPickerClose = () => {
    setAnchorEl(null);
  };

  // Validation function that returns true if all fields are valid
  const validateFields = () => {
    const newErrors = {
      routeId: !routeData.routeId,
      routeName: !routeData.routeName,
      origin: !routeData.origin,
      destination: !routeData.destination
    };

    setErrors(newErrors);

    // Return true if no errors (all fields filled)
    return !Object.values(newErrors).some(error => error);
  };

  // Handle input change with error clearing
  const handleInputChange = (field, value) => {
    onRouteDataChange(field, value);

    // Clear error when user enters value
    if (value && errors[field]) {
      setErrors({
        ...errors,
        [field]: false
      });
    }
  };

  // Wrapped submit handler to validate before calling the original handler
  const handleSubmit = () => {
    if (validateFields()) {
      onSubmit();
    }
  };


  return (
    <Box
      m={isSmallScreen ? 2 : 5}
      sx={{
        padding: isSmallScreen ? "15px" : "30px",
        borderRadius: 2,
        border: '1px solid #A5DCF3',
        boxShadow: '-2px 2px 5px 0px rgba(0,0,0,0.25)',
        backgroundColor: '#FFFFFF',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: isSmallScreen ? 'column' : 'row',
          gap: 2
        }}
      >
        {/* Left Section - Route Details */}
        <Box
          sx={{
            flexGrow: 1,
            minWidth: isSmallScreen ? "100%" : "550px",
            width: isSmallScreen ? "100%" : "auto"
          }}
        >
          <Grid container spacing={isSmallScreen ? 2 : 3}>
            {/* Header & Route Color in the Same Row */}
            <Grid item xs={12} md={6}>
              <Typography sx={{
                fontWeight: 500,
                fontFamily: 'Roboto',
                fontSize: isSmallScreen ? '20px' : '24px',
                lineHeight: '100%',
                color: '#183059',
              }}>{t("Route Details")}</Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: isSmallScreen ? 1 : 2,
                flexDirection: isExtraSmallScreen ? 'column' : 'row'
              }}>
                <Typography sx={{
                  fontWeight: 500,
                  fontFamily: 'Roboto',
                  fontSize: isSmallScreen ? '20px' : '24px',
                  lineHeight: '100%',
                  color: '#183059',
                  marginBottom: isExtraSmallScreen ? '10px' : 0
                }}>
                  {t("Select Route Colour")}
                </Typography>
                <Box
                  onClick={handleColorPickerOpen}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '4px',
                    backgroundColor: routeData.routeColor || "#ADD8E6",
                    cursor: 'pointer',
                    border: "2px solid #95A0B3"
                  }}
                />
              </Box>
            </Grid>

            {/* Route ID */}
            <Grid item xs={12} md={6}>
              <Typography>{t("Route ID")} <span style={{ color: "red" }}>*</span></Typography>
              <Input
                fullWidth
                value={routeData.routeId}
                placeholder='Type Route ID'
                onChange={(e) => handleInputChange('routeId', e.target.value)}
                error={errors.routeId}
                sx={{
                  height: 55,
                  borderRadius: "16px",
                  borderWidth: 2,
                  padding: "16px 24px",
                  background: "#FFFFFF",
                  border: "2px solid #95A0B3"
                }}
              />
              {errors.routeId && (
                <Typography color="error" variant="caption" sx={{ pl: 1 }}>
                  {t("Route ID is required")}
                </Typography>
              )}
            </Grid>

            {/* Route Name */}
            <Grid item xs={12} md={6}>
              <Typography>{t("Route Name")} <span style={{ color: "red" }}>*</span></Typography>
              <Input
                fullWidth
                value={routeData.routeName}
                placeholder='Type Route Name'
                onChange={(e) => handleInputChange('routeName', e.target.value)}
                error={errors.routeName}
                sx={{
                  height: 55,
                  borderRadius: "16px",
                  borderWidth: 2,
                  padding: "16px 24px",
                  background: "#FFFFFF",
                  border: "2px solid #95A0B3"
                }}
              />
              {errors.routeName && (
                <Typography color="error" variant="caption" sx={{ pl: 1 }}>
                  {t("Route Name is required")}
                </Typography>
              )}
            </Grid>

            {/* Origin */}
            <Grid item xs={12} md={6}>
              <Typography>{t("Origin")} <span style={{ color: "red" }}>*</span></Typography>
              <Select
                fullWidth
                value={routeData.origin?.id || ''}
                onChange={(e) => {
                  const selectedStop = stops.find(stop => stop.id === e.target.value);
                  handleInputChange('origin', selectedStop);
                }}
                error={errors.origin}
                sx={{
                  height: 55,
                  borderRadius: "16px",
                  borderWidth: 2,
                  padding: "16px 24px",
                  background: "#FFFFFF",
                  border: "2px solid #95A0B3"
                }}
                displayEmpty
              >
                <MenuItem value="">{t("Select Origin")}</MenuItem>
                {stops.map(stop => (
                  <MenuItem key={stop.id} value={stop.id}>
                    {stop.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.origin && (
                <Typography color="error" variant="caption" sx={{ pl: 1 }}>
                  {t("Origin is required")}
                </Typography>
              )}
            </Grid>

            {/* Destination */}
            <Grid item xs={12} md={6}>
              <Typography>{t("Destination")} <span style={{ color: "red" }}>*</span></Typography>
              <Select
                fullWidth
                value={routeData.destination?.id || ''}
                onChange={(e) => {
                  const selectedStop = stops.find(stop => stop.id === e.target.value);
                  handleInputChange('destination', selectedStop);
                }}
                error={errors.destination}
                sx={{
                  height: 55,
                  borderRadius: "16px",
                  borderWidth: 2,
                  padding: "16px 24px",
                  background: "#FFFFFF",
                  border: "2px solid #95A0B3"
                }}
                displayEmpty
              >
                <MenuItem value="">{t("Select Destination")}</MenuItem>
                {stops.map(stop => (
                  <MenuItem key={stop.id} value={stop.id}>
                    {stop.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.destination && (
                <Typography color="error" variant="caption" sx={{ pl: 1 }}>
                  {t("Destination is required")}
                </Typography>
              )}
            </Grid>
          </Grid>

          {/* Color Picker */}
          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handleColorPickerClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <ChromePicker
              color={routeData.routeColor}
              onChange={(color) => handleInputChange('routeColor', color.hex)}
            />
          </Popover>
        </Box>

        {/* Right Section - Vehicles */}
        <Box
          sx={{
            flexGrow: 1,
            width: isSmallScreen ? '100%' : 'calc(100% - 610px)'
          }}
        >
          <Box>
            <Box
              display="flex"
              flexDirection={isExtraSmallScreen ? 'column' : 'row'}
              justifyContent="space-between"
              alignItems="center"
              mb={2}
              sx={{
                background: '#E8EAEE',
                boxShadow: '0px 2px 5px 0px #00000040',
                padding: "10px 16px",
                gap: isExtraSmallScreen ? 2 : 0
              }}
            >
              <Typography variant="h6" sx={{
                color:"#183059",
                fontWeight:600,
                fontSize:"16px",
                marginBottom: isExtraSmallScreen ? '10px' : 0,
                textAlign: isExtraSmallScreen ? 'center' : 'left',
                width: isExtraSmallScreen ? '100%' : 'auto'
              }}>
                {t("Vehicles on this Route")}
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/vehicle/add')}
                sx={{
                  height: 47,
                  textTransform: "none",
                  minWidth: "auto",
                  padding: "8px 16px",
                  borderRadius: 1,
                  backgroundColor: '#F9E719',
                  color: "#1F242E",
                  fontSize: "16px",
                  whiteSpace: "nowrap",
                  '&:hover': { backgroundColor: '#F9E719' },
                  border: "1px solid #414958"
                }}
              >
                {t("+ Create Vehicle")}
              </Button>
            </Box>
            <FormControl fullWidth>
              <InputLabel
                id="stops-multiple-checkbox-label"
                sx={{
                  background: 'white',
                  marginTop: '-6px'
                }}
              >
                {t("Select Vehicles from the list")}
              </InputLabel>
              <Select
                fullWidth
                labelId="stops-multiple-checkbox-label"
                id="stops-multiple-checkbox"
                size='small'
                multiple
                value={routeData.selectedVehicles.map(v => v.id)}
                onChange={(e) => {
                  const selectedVehicleIds = e.target.value;
                  const selectedVehicles = vehicleList.filter(vehicle =>
                    selectedVehicleIds.includes(vehicle.id)
                  );
                  handleInputChange('selectedVehicles', selectedVehicles);
                }}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map(id => (
                      <Chip
                        key={id}
                        label={vehicleList.find(v => v.id === id)?.name}
                        size="small"
                      />
                    ))}
                  </Box>
                )}
                sx={{
                  minHeight: '40px',
                  '& .MuiSelect-select': {
                    display: 'flex',
                    flexWrap: 'wrap',
                    minHeight: '30px',
                    alignItems: 'center',
                    padding: '8px 14px',
                  }
                }}
              >
                {vehicleList.map(vehicle => (
                  <MenuItem key={vehicle.id} value={vehicle.id}>
                    <Checkbox checked={routeData.selectedVehicles.map(v => v.id).indexOf(vehicle.id) > -1} />
                    <ListItemText primary={vehicle.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Box>

      <Box sx={{
        display: 'flex',
        flexDirection: isExtraSmallScreen ? 'column' : 'row',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 2
      }}>
        <Button
          onClick={() => navigate('/route')}
          sx={{
            height: 55,
            width: isExtraSmallScreen ? "100%" : "122px",
            textTransform: "none",
            borderRadius: 1,
            fontSize:"20px",
            color:"#1F242E",
            backgroundColor: '#F2F2F3',
            border: '1px solid #414958',
            marginBottom: isExtraSmallScreen ? '10px' : 0
          }}
        >
          {t("Cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          sx={{
            height: 55,
            fontSize:"20px",
            color:"#1F242E",
            width: isExtraSmallScreen ? "100%" : "122px",
            textTransform: "none",
            borderRadius: 1,
            backgroundColor: '#F9E719',
            border: '1px solid #414958'
          }}
        >
          {t("Save")}
        </Button>
      </Box>
    </Box>

  );
}